// Account settings -> Sign-in methods (design revision 2026-10-19, D3-A). Shown only
// when text messages are on (SMS_MFA_ENABLED). People add or change a mobile number
// (consent box + texted confirmation code), pick where sign-in codes come from, set up
// the authenticator app, and see where password-reset codes go. The add-a-number
// section is the opt-in our carrier registration shows (docs/sms-mfa/optin-mockup.html):
// keep its wording identical.
import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchUserAttributes, fetchMFAPreference, updateMFAPreference, updateUserAttributes,
  sendUserAttributeVerificationCode, confirmUserAttribute, deleteUserAttributes, setUpTOTP, verifyTOTPSetup,
} from 'aws-amplify/auth';
import QRCode from 'qrcode';
import { useAuthOptions } from '../../utils/authOptions';
import { buildOtpauthUri } from '../../utils/otpauth';
import PhoneConsentFields from '../Sms/PhoneConsentFields';
import { toE164, formatPhone, badPhoneMessage } from '../Sms/smsConsent';
import { recordSmsConsent } from '../Sms/smsApi';
import ResendCodeButton from '../Login/ResendCodeButton';
import MfaCodeBoxes from '../Signup/MfaCodeBoxes';
import { setDefaultMethod, resetDestination } from './signInMethodsLogic';
import './SignInMethods.css';

const ERRORS = {
  CodeMismatchException: 'That code was incorrect. Enter the 6-digit code we sent.',
  ExpiredCodeException: 'That code expired. Send a new one.',
  LimitExceededException: 'Too many attempts. Please wait a few minutes and try again.',
  EnableSoftwareTokenMFAException: 'That code was incorrect. Enter the current 6-digit code from the app.',
};

function messageFor(err) {
  if (err?.userFacing) return err.message;
  return ERRORS[err?.name] || err?.response?.data?.error || 'Something went wrong. Please try again.';
}

export default function SignInMethods() {
  const options = useAuthOptions();
  const enabledOptions = options?.sms === true;
  const international = options?.sms_international === true;
  const [account, setAccount] = useState(null); // { email, phone, phoneVerified, enabled, preferred }
  const [mode, setMode] = useState('view');     // 'view' | 'number' | 'confirm' | 'app'
  const [phone, setPhone] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [pendingPhone, setPendingPhone] = useState('');
  const [code, setCode] = useState('');
  const [secret, setSecret] = useState('');
  const [qr, setQr] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const [attrs, mfa] = await Promise.all([fetchUserAttributes(), fetchMFAPreference()]);
      setAccount({
        email: attrs.email || '',
        phone: attrs.phone_number || '',
        phoneVerified: attrs.phone_number_verified === 'true',
        enabled: mfa?.enabled || [],
        preferred: mfa?.preferred,
      });
    } catch (err) {
      setError("We couldn't load your sign-in methods. Please try again.");
    }
  }, []);

  useEffect(() => {
    if (enabledOptions) load();
  }, [enabledOptions, load]);

  if (!enabledOptions) return null;

  // Runs `work` with the busy flag on; `onFail` tidies up after a failure.
  const run = async (work, onFail) => {
    setError('');
    setNotice('');
    setBusy(true);
    try {
      await work();
    } catch (err) {
      setError(messageFor(err));
      onFail?.();
    } finally {
      setBusy(false);
    }
  };

  const toView = () => {
    setMode('view');
    setCode('');
    setError('');
    setNotice('');
  };

  // Cancelling a pending number confirmation: Cognito may already hold the new number,
  // so show what it really has.
  const cancelConfirm = () => {
    setPhone('');
    setAgreed(false);
    toView();
    load();
  };

  // A different number needs its own consent: any edit unticks the box.
  const editPhone = (value) => {
    if (value !== phone) setAgreed(false);
    setPhone(value);
  };

  const startChange = () => {
    setError('');
    setPhone('');
    setAgreed(false);
    setMode('number');
  };

  if (!account) {
    return (
      <section className="sign-in-methods" aria-label="Sign-in methods">
        {error ? <p className="sim-error" role="alert">{error}</p> : <p className="sim-muted">Loading…</p>}
      </section>
    );
  }

  const hasApp = account.enabled.includes('TOTP');
  const hasNumber = Boolean(account.phone && account.phoneVerified);
  const current = account.preferred || (hasApp ? 'TOTP' : account.enabled[0]);

  const sendCode = async () => {
    const e164 = toE164(phone, { international });
    if (!e164) {
      setError(badPhoneMessage(international));
      return;
    }
    if (!agreed) {
      setError('Tick the box to agree to receive sign-in codes by text message.');
      return;
    }
    await run(async () => {
      const saved = await recordSmsConsent(e164);
      const result = await updateUserAttributes({ userAttributes: { phone_number: saved } });
      if (result?.phone_number?.nextStep?.updateAttributeStep !== 'CONFIRM_ATTRIBUTE_WITH_CODE') {
        await sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });
      }
      setPendingPhone(saved);
      setCode('');
      setMode('confirm');
    });
  };

  const confirmNumber = async (value = code) => {
    if (value.length !== 6) {
      setError('Enter the 6-digit code we texted you.');
      return;
    }
    if (busy) return;
    await run(async () => {
      await confirmUserAttribute({ userAttributeKey: 'phone_number', confirmationCode: value });
      setPhone('');
      setAgreed(false);
      toView();
      await load();
      setNotice('Your mobile number is confirmed.');
    }, () => setCode(''));
  };

  const resendNumberCode = () => sendUserAttributeVerificationCode({ userAttributeKey: 'phone_number' });

  const changeDefault = (method) => run(async () => {
    await setDefaultMethod(method, account.enabled, updateMFAPreference);
    await load();
    setNotice(method === 'sms'
      ? 'Sign-in codes now come by text message.'
      : 'Sign-in codes now come from your authenticator app.');
  });

  const removeNumber = async () => {
    if (current === 'SMS') {
      setError(hasApp
        ? 'Your sign-in codes come by text. Make the authenticator app your default first, then remove your number.'
        : 'Your sign-in codes come by text. Set up the authenticator app and make it your default first, then remove your number.');
      return;
    }
    if (!window.confirm(`Remove ${formatPhone(account.phone)}? Password-reset codes will go to your email.`)) return;
    await run(async () => {
      if (account.enabled.includes('SMS')) {
        try {
          await updateMFAPreference({ sms: 'DISABLED' });
        } catch (err) {
          const keep = new Error("We couldn't switch off text-message codes, so your number was kept. Please try again.");
          keep.userFacing = true;
          throw keep;
        }
      }
      await deleteUserAttributes({ userAttributeKeys: ['phone_number'] });
      await load();
      setNotice('Your mobile number was removed.');
    });
  };

  const startApp = () => run(async () => {
    const totp = await setUpTOTP();
    setSecret(totp.sharedSecret);
    setQr(await QRCode.toDataURL(
      buildOtpauthUri({ secret: totp.sharedSecret, email: account.email || 'account', issuer: 'RDV Systems' }),
      { width: 180, margin: 2 },
    ));
    setCode('');
    setMode('app');
  });

  const confirmApp = async (value = code) => {
    if (value.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }
    if (busy) return;
    await run(async () => {
      await verifyTOTPSetup({ code: value });
      // Verifying the token does not turn the app on as a sign-in method.
      await updateMFAPreference({ totp: current ? 'ENABLED' : 'PREFERRED' });
      toView();
      await load();
      setNotice('Your authenticator app is set up.');
    }, () => setCode(''));
  };

  const codeBoxes = (onComplete) => (
    <MfaCodeBoxes value={code} onChange={(next) => { setCode(next); if (error) setError(''); }}
      onComplete={onComplete} disabled={busy} hasError={!!error} />
  );

  const showOptIn = mode === 'number' || (mode === 'view' && !hasNumber);

  return (
    <section className="sign-in-methods" aria-label="Sign-in methods">
      {hasApp && hasNumber && mode === 'view' && (
        <fieldset className="sim-default">
          <legend>Sign-in codes come from</legend>
          <label className="sim-radio">
            <input type="radio" name="sim-default" checked={current === 'TOTP'}
              onChange={() => changeDefault('totp')} disabled={busy} />
            Authenticator app
          </label>
          <label className="sim-radio">
            <input type="radio" name="sim-default" checked={current === 'SMS'}
              onChange={() => changeDefault('sms')} disabled={busy} />
            Text message to {formatPhone(account.phone)}
          </label>
        </fieldset>
      )}

      {hasNumber && mode === 'view' && (
        <div className="sim-row">
          <span>Mobile number: <strong>{formatPhone(account.phone)}</strong></span>
          <span className="sim-row__actions">
            <button type="button" className="sim-link" onClick={startChange}
              disabled={busy}>Change</button>
            <button type="button" className="sim-link" onClick={removeNumber} disabled={busy}>Remove</button>
          </span>
        </div>
      )}

      {showOptIn && (
        <div className="sim-optin">
          <p className="sim-optin__title">
            {hasNumber ? 'Change your mobile number' : 'Add text-message verification'}
          </p>
          <p className="sim-optin__sub">
            Optional. Get sign-in and password-reset codes by text message. You can keep using your authenticator app instead.
          </p>
          <PhoneConsentFields idPrefix="account" phone={phone} onPhoneChange={editPhone}
            agreed={agreed} onAgreedChange={setAgreed} disabled={busy} />
          <button type="button" className="sim-primary" onClick={sendCode}
            disabled={busy || !phone.trim() || !agreed}>
            {busy ? 'Sending…' : 'Send code'}
          </button>
          <p className="sim-hint">We&apos;ll text a 6-digit code to this number to confirm it.</p>
          {hasNumber && (
            <button type="button" className="sim-link" onClick={toView} disabled={busy}>Cancel</button>
          )}
          <p className="sim-foot">
            Text messaging is not required to use the platform. You can remove your number at any time in Account settings.
          </p>
        </div>
      )}

      {mode === 'confirm' && (
        <div className="sim-step">
          <p>Enter the 6-digit code we texted to {formatPhone(pendingPhone)}.</p>
          {codeBoxes(confirmNumber)}
          <div className="sim-actions">
            <button type="button" className="sim-primary" onClick={() => confirmNumber()} disabled={busy}>
              {busy ? 'Checking…' : 'Confirm'}
            </button>
            <ResendCodeButton onResend={resendNumberCode}
              onError={() => setError("We couldn't send a new code. Please try again.")} />
            <button type="button" className="sim-link" onClick={cancelConfirm} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}

      {!hasApp && mode === 'view' && (
        <div className="sim-row">
          <span>Authenticator app: not set up</span>
          <button type="button" className="sim-link" onClick={startApp} disabled={busy}>
            Set up the authenticator app
          </button>
        </div>
      )}

      {mode === 'app' && (
        <div className="sim-step">
          <p>Scan the QR code with your authenticator app, then enter the 6-digit code it shows.</p>
          <img src={qr} alt="QR code for your authenticator app" className="sim-qr" />
          <p className="sim-hint">Can&apos;t scan it? Enter this key in the app: <code>{secret}</code></p>
          {codeBoxes(confirmApp)}
          <div className="sim-actions">
            <button type="button" className="sim-primary" onClick={() => confirmApp()} disabled={busy}>
              {busy ? 'Checking…' : 'Confirm'}
            </button>
            <button type="button" className="sim-link" onClick={toView} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}

      <p className="sim-muted">Password-reset codes are sent {resetDestination(account)}.</p>
      {notice && <p className="sim-notice" role="status">{notice}</p>}
      {error && <p className="sim-error" role="alert">{error}</p>}
    </section>
  );
}
